import { useState, useEffect, useRef } from 'react';
import { getPlayers, createPlayer, deletePlayer, sendWelcome, sendReminder, addTransaction, createSchedule, notifyTransaction } from '../api';
import { usePreferences } from '../context/PreferencesContext';
import { BulkRemindModal, RolloverModal, AnnouncementModal, RecurringReminderModal, downloadExport } from './ProjectActions';

export default function Dashboard({ onSelectPlayer, project }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null);
  const [quickAction, setQuickAction] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const { viewMode } = usePreferences();

  const load = async () => {
    try {
      const data = await getPlayers(project?.projectId);
      setPlayers(data);
      setSelected(new Set());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [project?.projectId]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete ${p.playerName}? This cannot be undone.`)) return;
    await deletePlayer(p.playerId);
    load();
  };

  const handleWelcome = async (p) => {
    if (!window.confirm(`Send welcome letter to ${p.parentEmails?.join(', ')}?`)) return;
    try {
      await sendWelcome(p.playerId);
      showToast(`✓ Welcome letter sent to ${p.playerName}`);
    } catch (err) { setError(`Failed to send welcome: ${err.message}`); }
  };

  const handleRemind = async (p) => {
    if (!window.confirm(`Send payment reminder to ${p.parentEmails?.join(', ')}?`)) return;
    try {
      await sendReminder(p.playerId, '');
      showToast(`✓ Reminder sent to ${p.playerName}`);
    } catch (err) { setError(`Failed to send reminder: ${err.message}`); }
  };

  // Multi-select
  const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === players.length ? new Set() : new Set(players.map(p => p.playerId)));
  const selectedPlayers = players.filter(p => selected.has(p.playerId));

  const totalOwed    = players.reduce((s, p) => s + Math.max(0, p.currentBalance), 0);
  const totalPaid    = players.reduce((s, p) => s + (p.totalCollected || 0), 0);
  const playersOwing = players.filter(p => p.currentBalance > 0).length;

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}
      {toast && <div className="alert alert-success">{toast}</div>}

      <div className="stat-row">
        <div className="stat-box"><div className="stat-label">Players</div><div className="stat-value">{players.length}</div></div>
        <div className="stat-box"><div className="stat-label">Still Owe</div><div className="stat-value" style={{ color: 'var(--red)' }}>{playersOwing}</div></div>
        <div className="stat-box"><div className="stat-label">Outstanding</div><div className="stat-value balance-owed">${totalOwed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
        <div className="stat-box"><div className="stat-label">Collected</div><div className="stat-value balance-paid">${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
      </div>

      <div className="page-header">
        <div>
          <h2>{project?.name || 'All Players'}</h2>
          {project?.season && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{project.season}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {project && <>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('bulkRemind')}>📧 Remind All</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('announcement')}>
              📢 {project.announcement?.text ? 'Edit Announcement' : 'Announcement'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('recurring')}>
              ⏰ {project.recurringReminder?.enabled ? 'Auto-Remind On' : 'Auto-Remind'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('rollover')}>🔄 New Season</button>
            <button className="btn btn-ghost btn-sm" onClick={() => downloadExport(project)}>📥 Export</button>
          </>}
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add Player</button>
        </div>
      </div>

      {project?.announcement?.text && (
        <div style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid var(--yellow)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>📢</span><span style={{ fontSize: 14 }}>{project.announcement.text}</span>
        </div>
      )}

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div style={{ background: 'var(--navy)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>{selected.size} selected</span>
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={() => setBulkModal('welcome')}>✉️ Send Welcome</button>
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={() => setBulkModal('remind')}>📧 Send Reminder</button>
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={() => setBulkModal('expense')}>+ Add Expense</button>
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }} onClick={() => setSelected(new Set())}>✕ Clear</button>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : players.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: 'var(--muted)', marginBottom: 16 }}>No players yet.</p>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>Add First Player</button>
        </div>
      ) : viewMode === 'list' ? (
        <table className="list-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={selected.size === players.length && players.length > 0}
                  onChange={toggleAll} style={{ cursor: 'pointer' }} />
              </th>
              <th>Player</th>
              <th>Total Owed</th>
              <th>Outstanding</th>
              <th>Paid</th>
              <th>Schedules</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {players.map(p => {
              const balance = p.currentBalance;
              const collected = p.totalCollected || 0;
              const totalOwed = collected + balance;
              const pct = totalOwed > 0 ? Math.min(100, Math.round((collected / totalOwed) * 100)) : 0;
              return (
                <tr key={p.playerId} style={{ background: selected.has(p.playerId) ? 'rgba(59,130,246,0.08)' : undefined }}>
                  <td>
                    <input type="checkbox" checked={selected.has(p.playerId)}
                      onChange={() => toggleSelect(p.playerId)} style={{ cursor: 'pointer' }} />
                  </td>
                  <td>
                    <div style={{ fontWeight: 700, cursor: 'pointer', color: 'var(--text)' }} onClick={() => onSelectPlayer(p.playerId)}>{p.playerName}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.parentEmails?.join(', ') || 'No email'}</div>
                  </td>
                  <td style={{ fontWeight: 700 }}>${totalOwed.toFixed(2)}</td>
                  <td>
                    <span style={{ fontWeight: 700, color: balance > 0 ? 'var(--red)' : 'var(--green)' }}>
                      {balance > 0 ? `$${balance.toFixed(2)}` : `✓ Paid`}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, width: 60, overflow: 'hidden' }}>
                        <div style={{ background: balance <= 0 ? 'var(--green)' : 'var(--navy)', width: `${pct}%`, height: '100%', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{pct}%</span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: 13 }}>{p.paymentSchedules?.length || 0}</td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-xs btn-ghost" onClick={() => onSelectPlayer(p.playerId)}>Open</button>
                      <button className="btn btn-xs btn-ghost" onClick={() => setQuickAction({ player: p, type: 'payment' })}>+ Pay</button>
                      <button className="btn btn-xs btn-ghost" onClick={() => setQuickAction({ player: p, type: 'expense' })}>+ Expense</button>
                      <button className="btn btn-xs btn-ghost" onClick={() => setQuickAction({ player: p, type: 'schedule' })}>+ Schedule</button>
                      {p.parentEmails?.length > 0 && <NotifyDropdown onWelcome={() => handleWelcome(p)} onRemind={() => handleRemind(p)} />}
                      <button className="btn btn-xs" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--red)' }} onClick={() => handleDelete(p)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="player-grid">
          {players.map(p => (
            <PlayerCard key={p.playerId} player={p}
              onClick={() => onSelectPlayer(p.playerId)}
              onDelete={() => handleDelete(p)}
              onQuickAction={(type) => setQuickAction({ player: p, type })} />
          ))}
        </div>
      )}

      {/* Project modals */}
      {showAdd && <AddPlayerModal projectId={project?.projectId} onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); load(); }} />}
      {modal === 'bulkRemind' && <BulkRemindModal project={project} playerCount={players.length} onClose={() => setModal(null)} />}
      {modal === 'rollover' && <RolloverModal project={project} onClose={() => setModal(null)} onSave={() => setModal(null)} />}
      {modal === 'announcement' && <AnnouncementModal project={project} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
      {modal === 'recurring' && <RecurringReminderModal project={project} onClose={() => setModal(null)} onSave={() => setModal(null)} />}

      {/* Bulk action modals */}
      {bulkModal && <BulkActionModal type={bulkModal} players={selectedPlayers} onClose={() => setBulkModal(null)} onDone={() => { setBulkModal(null); setSelected(new Set()); load(); }} />}

      {/* Per-player quick action modal */}
      {quickAction && <QuickActionModal player={quickAction.player} type={quickAction.type} schedules={quickAction.player.paymentSchedules || []} onClose={() => setQuickAction(null)} onSave={() => { setQuickAction(null); load(); }} />}
    </div>
  );
}

// ─── Notify Dropdown ─────────────────────────────────────────────────────────

function NotifyDropdown({ onWelcome, onRemind }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const itemStyle = { display: 'block', width: '100%', padding: '9px 14px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, color: 'var(--text)', cursor: 'pointer' };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn btn-xs btn-ghost" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
        Notify ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.25)', minWidth: 160, marginTop: 4 }}>
          <button style={itemStyle}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={e => { e.stopPropagation(); setOpen(false); onWelcome(); }}>
            ✉️ Welcome Letter
          </button>
          <button style={itemStyle}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={e => { e.stopPropagation(); setOpen(false); onRemind(); }}>
            📧 Payment Reminder
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Player Card ──────────────────────────────────────────────────────────────

function PlayerCard({ player, onClick, onDelete, onQuickAction }) {
  const balance = player.currentBalance;
  const collected = player.totalCollected || 0;
  const totalOwed = collected + balance;
  const pct = totalOwed > 0 ? Math.min(100, Math.round((collected / totalOwed) * 100)) : 0;

  return (
    <div className="player-card" onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="player-name">{player.playerName}</div>
        <button className="btn btn-sm" style={{ background: 'none', color: 'var(--muted)', fontSize: 16, padding: '2px 6px' }}
          onClick={e => { e.stopPropagation(); onDelete(); }}>×</button>
      </div>
      <div className={`balance ${balance > 0 ? 'balance-owed' : 'balance-paid'}`}>
        {balance > 0 ? `-$${balance.toFixed(2)}` : `✓ $${Math.abs(balance).toFixed(2)}`}
      </div>
      <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, margin: '8px 0' }}>
        <div style={{ background: balance <= 0 ? 'var(--green)' : 'var(--navy)', width: `${pct}%`, height: '100%', borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
      <div className="meta" style={{ marginBottom: 10 }}>
        Total owed: ${totalOwed.toFixed(2)} · {pct}% paid
        {player.paymentSchedules?.length > 0 && <span style={{ marginLeft: 8 }}>· 📋 {player.paymentSchedules.length}</span>}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
        <button className="btn btn-xs btn-ghost" onClick={() => onQuickAction('payment')}>+ Payment</button>
        <button className="btn btn-xs btn-ghost" onClick={() => onQuickAction('expense')}>+ Expense</button>
        <button className="btn btn-xs btn-ghost" onClick={() => onQuickAction('schedule')}>+ Schedule</button>
      </div>
    </div>
  );
}

// ─── Quick Action Modal (per-player from listing) ─────────────────────────────

function QuickActionModal({ player, type, schedules, onClose, onSave }) {
  const [form, setForm] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0], scheduleId: '', notifyParent: false });
  const [scheduleForm, setScheduleForm] = useState({ title: '', rows: [{ description: '', dueDate: '', amount: '' }] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const titles = { payment: '+ Record Payment', expense: '+ Add Expense', schedule: '+ Add Schedule' };

  const handleTransaction = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await addTransaction(player.playerId, { type, amount: parseFloat(form.amount), description: form.description, date: form.date, scheduleId: form.scheduleId || null });
      if (form.notifyParent) await notifyTransaction(player.playerId, type, form.amount, form.description).catch(() => {});
      onSave();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleForm.title) { setError('Title required'); return; }
    if (scheduleForm.rows.some(r => !r.description || !r.dueDate || !r.amount)) { setError('All rows need description, date, and amount'); return; }
    setError(''); setLoading(true);
    try {
      await createSchedule(player.playerId, { title: scheduleForm.title, schedule: scheduleForm.rows.map(r => ({ ...r, amount: parseFloat(r.amount) })) });
      onSave();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const updateRow = (i, k, v) => setScheduleForm(f => ({ ...f, rows: f.rows.map((r, ri) => ri === i ? { ...r, [k]: v } : r) }));
  const addRow = () => setScheduleForm(f => ({ ...f, rows: [...f.rows, { description: '', dueDate: '', amount: '' }] }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: type === 'schedule' ? 580 : 420 }} onClick={e => e.stopPropagation()}>
        <h3>{titles[type]} — {player.playerName}</h3>
        {error && <div className="alert alert-error">{error}</div>}

        {type !== 'schedule' ? (
          <form onSubmit={handleTransaction}>
            <div className="form-row">
              <div className="form-group">
                <label>Amount ($)</label>
                <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required autoFocus placeholder="0.00" />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={form.date} onChange={set('date')} required />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <input value={form.description} onChange={set('description')} placeholder="Optional note…" />
            </div>
            {type === 'payment' && schedules.length > 0 && (
              <div className="form-group">
                <label>Apply to Schedule (optional)</label>
                <select value={form.scheduleId} onChange={set('scheduleId')}>
                  <option value="">— Just apply to balance —</option>
                  {schedules.map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.title}</option>)}
                </select>
              </div>
            )}
            <div className="form-group" style={{ marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.notifyParent}
                  onChange={e => setForm(f => ({ ...f, notifyParent: e.target.checked }))}
                  style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
                <span>Notify parent by email</span>
              </label>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSchedule}>
            <div className="form-group">
              <label>Schedule Title</label>
              <input value={scheduleForm.title} onChange={e => setScheduleForm(f => ({ ...f, title: e.target.value }))} required autoFocus placeholder="e.g. Monthly Payments" />
            </div>
            <div style={{ border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px 32px', gap: 8, padding: '8px 12px', background: 'var(--navy)', color: 'white', fontSize: 12, fontWeight: 700 }}>
                <span>Description</span><span>Due Date</span><span>Amount ($)</span><span></span>
              </div>
              {scheduleForm.rows.map((row, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px 32px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                  <input value={row.description} onChange={e => updateRow(i, 'description', e.target.value)}
                    placeholder="e.g. Payment 1" style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                  <input type="date" value={row.dueDate} onChange={e => updateRow(i, 'dueDate', e.target.value)}
                    style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                  <input type="number" step="0.01" value={row.amount} onChange={e => updateRow(i, 'amount', e.target.value)}
                    placeholder="0.00" style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                  <button type="button" onClick={() => setScheduleForm(f => ({ ...f, rows: f.rows.filter((_, ri) => ri !== i) }))} disabled={scheduleForm.rows.length === 1}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 18 }}>×</button>
                </div>
              ))}
              <div style={{ padding: '8px 12px' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={addRow}>+ Add Row</button>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : 'Add Schedule'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Bulk Action Modal ────────────────────────────────────────────────────────

function BulkActionModal({ type, players, onClose, onDone }) {
  const [message, setMessage] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const withEmails = players.filter(p => p.parentEmails?.length > 0);

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (type === 'welcome') {
        await Promise.allSettled(withEmails.map(p => sendWelcome(p.playerId, message)));
        setResults({ sent: withEmails.length, skipped: players.length - withEmails.length });
      } else if (type === 'remind') {
        await Promise.allSettled(withEmails.map(p => sendReminder(p.playerId, message)));
        setResults({ sent: withEmails.length, skipped: players.length - withEmails.length });
      } else if (type === 'expense') {
        const date = new Date().toISOString().split('T')[0];
        await Promise.allSettled(players.map(p => addTransaction(p.playerId, { type: 'expense', amount: parseFloat(amount), description, date })));
        setResults({ sent: players.length, skipped: 0 });
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const titles = { welcome: '✉️ Bulk Welcome', remind: '📧 Bulk Reminder', expense: '+ Bulk Expense' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{titles[type]}</h3>

        {results ? (
          <div>
            <div className="alert alert-success">
              ✓ Done — {results.sent} sent{results.skipped > 0 ? `, ${results.skipped} skipped (no email)` : ''}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onDone}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handle}>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              {type === 'expense'
                ? `Adding an expense to ${players.length} player${players.length !== 1 ? 's' : ''}.`
                : `Sending to ${withEmails.length} of ${players.length} selected player${players.length !== 1 ? 's' : ''} with email addresses.`}
            </p>
            {error && <div className="alert alert-error">{error}</div>}

            {type === 'expense' ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount ($)</label>
                    <input type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required autoFocus placeholder="0.00" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Tournament fee" />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label>Custom Message (optional)</label>
                <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a personal note…" />
              </div>
            )}

            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading || (type !== 'expense' && withEmails.length === 0)}>
                {loading ? 'Processing…' : type === 'expense' ? `Add to ${players.length} Players` : `Send to ${withEmails.length} Players`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Add Player Modal ─────────────────────────────────────────────────────────

function AddPlayerModal({ projectId, onClose, onSave }) {
  const [form, setForm] = useState({ playerName: '', parentEmails: '', startingBalance: '', notes: '', sendWelcomeLetter: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const emails = form.parentEmails.split(',').map(s => s.trim()).filter(Boolean);
      const player = await createPlayer({ playerName: form.playerName, parentEmails: emails, startingBalance: parseFloat(form.startingBalance) || 0, notes: form.notes, projectId: projectId || null });
      if (form.sendWelcomeLetter && emails.length > 0) await sendWelcome(player.playerId);
      onSave();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const hasEmails = form.parentEmails.trim().length > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Add Player</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group"><label>Player Name</label><input value={form.playerName} onChange={set('playerName')} required autoFocus placeholder="e.g. Jake Smith" /></div>
          <div className="form-group"><label>Starting Balance ($)</label><input type="number" step="0.01" min="0" value={form.startingBalance} onChange={set('startingBalance')} placeholder="0.00" /><div className="helper-text">Total amount owed for the season. Must be 0 or greater.</div></div>
          <div className="form-group"><label>Parent Email(s)</label><input value={form.parentEmails} onChange={set('parentEmails')} placeholder="mom@email.com, dad@email.com" /><div className="helper-text">Separate multiple with commas</div></div>
          <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={set('notes')} rows={2} placeholder="Optional notes…" /></div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 14, fontWeight: 500, cursor: hasEmails ? 'pointer' : 'not-allowed', opacity: hasEmails ? 1 : 0.5, width: '100%' }}>
              <input type="checkbox" checked={form.sendWelcomeLetter} disabled={!hasEmails} onChange={e => setForm(f => ({ ...f, sendWelcomeLetter: e.target.checked }))} style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
              <span>Send welcome letter after adding</span>
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : 'Add Player'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
